<template>
  <v-dialog v-model="dialog" max-width="1400px" scrollable>
    <v-card>
      <v-card-title class="headline">
        <v-icon left color="primary">mdi-file-chart</v-icon>
        eIzvještavanje
        <v-spacer></v-spacer>
        <v-btn icon @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-0">
        <v-data-table
          :headers="headers"
          :items="requests"
          :loading="loading"
          loading-text="Učitavam zahtjeve..."
          no-data-text="Nema podataka"
          class="elevation-0"
          show-expand
          :single-expand="true"
          item-key="createdOn"
          sort-by="createdOn"
          :sort-desc="true"
        >
          <template v-slot:item.createdOn="{ item }">
            {{ formatDateTime(item.createdOn) }}
          </template>

          <template v-slot:item.completedOn="{ item }">
            {{ formatDateTime(item.completedOn) }}
          </template>

          <template v-slot:item.type="{ item }">
            {{ getRequestTypeText(item.type) }}
          </template>

          <template v-slot:item.paymentDate="{ item }">
            <span v-if="item.paymentDate">{{
              formatDate(item.paymentDate)
            }}</span>
            <span v-else class="text--disabled">-</span>
          </template>

          <template v-slot:item.paymentType="{ item }">
            <span v-if="item.paymentType">{{
              getPaymentTypeText(item.paymentType)
            }}</span>
            <span v-else class="text--disabled">-</span>
          </template>

          <template v-slot:item.paidAmount="{ item }">
            <span v-if="item.paidAmount != null">{{
              formatCurrency(item.paidAmount)
            }}</span>
            <span v-else class="text--disabled">-</span>
          </template>

          <template v-slot:item.success="{ item }">
            <v-chip
              :color="item.success ? 'success' : 'error'"
              text-color="white"
              small
            >
              <v-icon v-if="!item.success && item.errorCode" left x-small>
                mdi-alert-circle
              </v-icon>
              {{ item.success ? "Uspjeh" : "Neuspjeh" }}
            </v-chip>
          </template>

          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-4">
              <v-alert
                v-if="!item.success && (item.errorCode || item.errorDescription)"
                type="error"
                dense
                outlined
                class="mb-0"
              >
                <div class="d-flex flex-column">
                  <div v-if="item.errorCode" class="mb-2">
                    <strong>Kod greške:</strong> {{ item.errorCode }}
                  </div>
                  <div v-if="item.errorDescription">
                    <strong>Opis greške:</strong> {{ item.errorDescription }}
                  </div>
                </div>
              </v-alert>
              <v-alert
                v-else-if="item.success"
                type="success"
                dense
                outlined
                class="mb-0"
              >
                <div>
                  <strong>Zahtjev uspješno izvršen</strong>
                </div>
                <div v-if="item.referenceId" class="mt-2 text--secondary">
                  ID: {{ item.referenceId }}
                </div>
              </v-alert>
              <div v-else class="text--secondary">
                Nema dodatnih informacija
              </div>
            </td>
          </template>
        </v-data-table>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="dialog = false"> Zatvori </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { ereportingApi } from '@/api/ereporting'
import { DateTime } from 'luxon'

export default {
  name: 'EReportingDialog',

  data () {
    return {
      dialog: false,
      loading: false,
      requests: [],
      expanded: [],
      headers: [
        {
          text: 'Stvoren',
          value: 'createdOn',
          sortable: true
        },
        {
          text: 'Završen',
          value: 'completedOn',
          sortable: true
        },
        {
          text: 'Tip',
          value: 'type',
          sortable: true
        },
        {
          text: 'Opis',
          value: 'description',
          sortable: false
        },
        {
          text: 'Datum plaćanja',
          value: 'paymentDate',
          sortable: true
        },
        {
          text: 'Tip plaćanja',
          value: 'paymentType',
          sortable: false
        },
        {
          text: 'Plaćeni iznos',
          value: 'paidAmount',
          sortable: true
        },
        {
          text: 'Status',
          value: 'success',
          sortable: true
        }
      ]
    }
  },

  methods: {
    async show (document) {
      this.dialog = true
      this.loading = true
      this.requests = []

      try {
        const data = await ereportingApi.getEReportingRequests(
          document.documentId,
          this.getIssueYear(document),
          document.companyId
        )
        this.requests = data?.requests || []
      } catch (error) {
        console.error('Error loading eReporting requests:', error)
        this.$toast.error('Greška pri učitavanju eReporting zahtjeva')
      } finally {
        this.loading = false
      }
    },

    getIssueYear (document) {
      if (document.issueDate) {
        return new Date(document.issueDate).getFullYear()
      }
      if (document.dateTimeIssued) {
        return new Date(document.dateTimeIssued).getFullYear()
      }
      return new Date().getFullYear()
    },

    formatDateTime (timestamp) {
      if (!timestamp) return '-'
      return DateTime.fromISO(timestamp).toFormat('dd.MM.yyyy HH:mm')
    },

    formatDate (date) {
      if (!date) return '-'
      return DateTime.fromISO(date).toFormat('dd.MM.yyyy')
    },

    getRequestTypeText (type) {
      const typeMap = {
        1: 'Evidencija izlaznog računa',
        2: 'Evidencija ulaznog računa',
        3: 'Storniranje računa',
        4: 'Evidencija naplate',
        5: 'Storniranje naplate'
      }
      return typeMap[type] || `Tip ${type}`
    },

    getPaymentTypeText (type) {
      const typeMap = {
        1: 'Transakcijski račun (T)',
        2: 'Obračunsko plaćanje (O)',
        3: 'Gotovina (G)',
        4: 'Kartica (K)',
        5: 'Ček (C)',
        6: 'Ostalo (Z)'
      }
      return typeMap[type] || `Tip ${type}`
    },

    formatCurrency (amount) {
      if (amount == null) return '-'
      return new Intl.NumberFormat('hr-HR', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount)
    }
  }
}
</script>
